import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './NFLProject.css';

gsap.registerPlugin(ScrollTrigger);

const I = '/content/nfl/images/';

// ── Data ────────────────────────────────────────────────────────────

const OUTCOMES = [
  {
    tag: '40-page guidebook',
    title: 'Adopted by Indiana University\'s Sports Data Analytics course.',
    body: 'Used by 34 students. Reduced NFL Big Data Bowl competition onboarding by 2 weeks by consolidating all data visualization examples into a single comprehensive resource.',
  },
  {
    tag: 'Formation classifier',
    title: '87% accuracy predicting offensive formations.',
    body: 'K-means clustering on pre-snap spatial features identifies shotgun, singleback, trips, and 9 other formation types from raw coordinates.',
  },
  {
    tag: 'Visualization suite',
    title: '6 analysis modules across Python and R.',
    body: 'KDE heatmaps, correlation matrices, QB decision zone scatterplots, movement efficiency ratios, time series trajectories, and rushing distributions.',
  },
  {
    tag: 'Temporal insight',
    title: 'QBs who release past 3 seconds are 23% less accurate.',
    body: 'Time-to-throw computed by counting frames between snap and release events, then correlated with pass completion percentage.',
  },
];

const DATASETS = [
  {
    file: 'games.csv',
    dims: '272 × 9',
    desc: 'Game-level metadata: dates, teams, home and away. The outermost context layer that anchors every downstream join.',
  },
  {
    file: 'players.csv',
    dims: '1,697 × 7',
    desc: 'One record per active-season player: position, height, weight, college. 32 teams × ~53 roster slots = 1,697 unique players who appeared across the tracked weeks.',
  },
  {
    file: 'plays.csv',
    dims: '31,040 × 35',
    desc: 'Play-by-play: formation, down, distance, yards gained, play result. The event layer between games and tracking frames.',
  },
  {
    file: 'tracking_week_[1-9]',
    dims: '~8M rows × 15 per week',
    desc: 'x, y, speed, acceleration, orientation. 10 Hz. Every player. Every 0.1 seconds. This is the signal.',
  },
];

const REFLECTIONS = [
  {
    num: '01',
    text: 'Visualization is a design decision. Choosing KDE over a histogram, coolwarm over viridis, is not arbitrary. Each choice encodes an argument about what the reader should notice first.',
  },
  {
    num: '02',
    text: 'The guidebook is the product. Anyone can run the analysis. Making it legible to a student who has never opened a CSV requires a different kind of thinking, closer to UX writing than data science.',
  },
  {
    num: '03',
    text: 'Domain knowledge is not optional. The formation classifier only works because someone understood that 11 players at the line of scrimmage means something different than 7. The model does not know that. You have to.',
  },
];

// ── Syntax Highlighter ───────────────────────────────────────────────

const highlightCode = (code: string, lang: string): React.ReactElement => {
  const lines = code.split('\n');

  const highlightLine = (line: string, lineIndex: number): React.ReactElement => {
    let result: React.ReactElement[] = [];
    let remaining = line;
    let key = 0;

    const keywords = lang === 'Python'
      ? ['import', 'from', 'as', 'def', 'class', 'if', 'else', 'elif', 'for', 'while', 'in', 'return', 'try', 'except', 'with', 'lambda', 'yield', 'True', 'False', 'None', 'and', 'or', 'not']
      : ['library', 'function', 'if', 'else', 'for', 'while', 'in', 'return', 'TRUE', 'FALSE', 'NULL', 'NA'];

    const builtins = lang === 'Python'
      ? ['print', 'len', 'range', 'sum', 'mean', 'str', 'int', 'float', 'list', 'dict', 'set', 'plt', 'pd', 'np', 'sns']
      : ['print', 'cat', 'mean', 'median', 'sum', 'length', 'ggplot', 'aes', 'geom_', 'read.csv', 'summarise', 'mutate', 'filter', 'group_by'];

    while (remaining.length > 0) {
      if (remaining.startsWith('#')) {
        result.push(<span key={`${lineIndex}-${key++}`} className="syntax-comment">{remaining}</span>);
        break;
      }
      if (remaining.startsWith('"')) {
        const endQuote = remaining.indexOf('"', 1);
        if (endQuote !== -1) {
          const str = remaining.substring(0, endQuote + 1);
          result.push(<span key={`${lineIndex}-${key++}`} className="syntax-string">{str}</span>);
          remaining = remaining.substring(endQuote + 1);
          continue;
        }
      }
      if (remaining.startsWith("'")) {
        const endQuote = remaining.indexOf("'", 1);
        if (endQuote !== -1) {
          const str = remaining.substring(0, endQuote + 1);
          result.push(<span key={`${lineIndex}-${key++}`} className="syntax-string">{str}</span>);
          remaining = remaining.substring(endQuote + 1);
          continue;
        }
      }
      if (lang === 'Python' && remaining.startsWith('f"')) {
        const endQuote = remaining.indexOf('"', 2);
        if (endQuote !== -1) {
          const str = remaining.substring(0, endQuote + 1);
          result.push(<span key={`${lineIndex}-${key++}`} className="syntax-string">{str}</span>);
          remaining = remaining.substring(endQuote + 1);
          continue;
        }
      }
      const numMatch = remaining.match(/^(\d+\.?\d*)/);
      if (numMatch) {
        result.push(<span key={`${lineIndex}-${key++}`} className="syntax-number">{numMatch[0]}</span>);
        remaining = remaining.substring(numMatch[0].length);
        continue;
      }
      let keywordFound = false;
      for (const keyword of keywords) {
        if (remaining.startsWith(keyword) && (remaining.length === keyword.length || !/[a-zA-Z_]/.test(remaining[keyword.length]))) {
          result.push(<span key={`${lineIndex}-${key++}`} className="syntax-keyword">{keyword}</span>);
          remaining = remaining.substring(keyword.length);
          keywordFound = true;
          break;
        }
      }
      if (keywordFound) continue;
      let builtinFound = false;
      for (const builtin of builtins) {
        if (remaining.startsWith(builtin)) {
          result.push(<span key={`${lineIndex}-${key++}`} className="syntax-builtin">{builtin}</span>);
          remaining = remaining.substring(builtin.length);
          builtinFound = true;
          break;
        }
      }
      if (builtinFound) continue;
      const funcMatch = remaining.match(/^([a-zA-Z_][a-zA-Z0-9_\.]*)\s*\(/);
      if (funcMatch) {
        result.push(<span key={`${lineIndex}-${key++}`} className="syntax-function">{funcMatch[1]}</span>);
        remaining = remaining.substring(funcMatch[1].length);
        continue;
      }
      result.push(<span key={`${lineIndex}-${key++}`}>{remaining[0]}</span>);
      remaining = remaining.substring(1);
    }
    return <>{result}</>;
  };

  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {highlightLine(line, index)}
          {index < lines.length - 1 && '\n'}
        </React.Fragment>
      ))}
    </>
  );
};

// ── Code Block Component ─────────────────────────────────────────────

const CodeBlock: React.FC<{ lang: string; filename: string; code: string }> = ({ lang, filename, code }) => {
  const [copied, setCopied] = useState(false);
  const [displayedCode, setDisplayedCode] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { rootMargin: '-50px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  useEffect(() => {
    if (!isInView) return;
    setIsTyping(true);
    let currentIndex = 0;
    const typeInterval = setInterval(() => {
      if (currentIndex <= code.length) {
        setDisplayedCode(code.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, 8);
    return () => clearInterval(typeInterval);
  }, [isInView, code]);

  useEffect(() => {
    if (!isTyping) { setShowCursor(false); return; }
    const cursorInterval = setInterval(() => { setShowCursor(prev => !prev); }, 530);
    return () => clearInterval(cursorInterval);
  }, [isTyping]);

  return (
    <div ref={ref} className="nfl-code-block">
      <div className="nfl-code-header">
        <div>
          <span className="nfl-code-lang">{lang}</span>
          <span className="nfl-code-file">{filename}</span>
        </div>
        <button className={`nfl-code-copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
          {copied ? (
            <svg className="nfl-code-copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" fill="currentColor"/>
            </svg>
          ) : (
            <svg className="nfl-code-copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z" fill="currentColor"/>
              <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z" fill="currentColor"/>
            </svg>
          )}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <pre className="nfl-code">
        {highlightCode(displayedCode, lang)}
        {isTyping && showCursor && (
          <span style={{ display: 'inline-block', width: '8px', height: '1em', backgroundColor: '#50fa7b', marginLeft: '2px' }}>│</span>
        )}
      </pre>
    </div>
  );
};

// ── Chart Components ─────────────────────────────────────────────────

const SPEED_BINS = [100, 21, 11, 7, 4.5, 2.8, 1.7, 1.0, 0.55, 0.28];

const SpeedDistributionChart: React.FC = () => (
  <div className="nfl__chart-wrap">
    <div className="nfl__chart-title">Speed Distribution — Weeks 1–9 (yards / sec)</div>
    <div className="nfl__speed-grid">
      {Array.from({ length: 9 }, (_, i) => (
        <div key={i} className="nfl__speed-mini">
          <span className="nfl__speed-week">Week {i + 1}</span>
          <div className="nfl__speed-bars">
            {SPEED_BINS.map((h, j) => (
              <div key={j} className="nfl__speed-bar" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="nfl__speed-axis">
            <span>0</span><span>5</span><span>10+</span>
          </div>
        </div>
      ))}
    </div>
    <p className="nfl__chart-note">Consistent right-skewed shape across all 9 weeks — a data quality signal. A shifted or flattened week would indicate RFID dropout.</p>
  </div>
);

const CORR_LABELS = ['speed', 'accel', 'dist'];
const CORR_MATRIX = [
  [1.00, 0.58, 0.98],
  [0.58, 1.00, 0.58],
  [0.98, 0.58, 1.00],
];

const CorrelationMatrixChart: React.FC = () => (
  <div className="nfl__chart-wrap">
    <div className="nfl__chart-title">Correlation Matrix — speed · acceleration · distance</div>
    <div className="nfl__corr-table">
      <div className="nfl__corr-header">
        <div />
        {CORR_LABELS.map(l => <div key={l} className="nfl__corr-label-top">{l}</div>)}
      </div>
      {CORR_MATRIX.map((row, i) => (
        <div key={i} className="nfl__corr-row">
          <div className="nfl__corr-label-left">{CORR_LABELS[i]}</div>
          {row.map((val, j) => (
            <div
              key={j}
              className="nfl__corr-cell"
              style={{
                background: `rgba(183,24,83,${0.08 + val * 0.88})`,
                color: val > 0.7 ? '#fff' : '#1a1a1a',
              }}
            >
              {val.toFixed(2)}
            </div>
          ))}
        </div>
      ))}
    </div>
    <p className="nfl__chart-note">Speed–distance at 0.98 is near-physical law. Acceleration at 0.58 is where position roles diverge — linemen and receivers share speed; they don't share acceleration profiles.</p>
  </div>
);

const COMPLETENESS_COLS = [
  { name: 'nflId',       count: 1697 },
  { name: 'displayName', count: 1697 },
  { name: 'position',    count: 1697 },
  { name: 'height',      count: 1697 },
  { name: 'weight',      count: 1697 },
  { name: 'birthDate',   count: 1697 },
  { name: 'collegeName', count: 1697 },
];

const DataCompletenessChart: React.FC = () => (
  <div className="nfl__chart-wrap">
    <div className="nfl__chart-title">players.csv — Data Completeness (n = 1,697)</div>
    <div className="nfl__completeness">
      {COMPLETENESS_COLS.map(c => (
        <div key={c.name} className="nfl__complete-row">
          <span className="nfl__complete-name">{c.name}</span>
          <div className="nfl__complete-track">
            <div className="nfl__complete-fill" style={{ width: `${(c.count / 1697) * 100}%` }} />
          </div>
          <span className="nfl__complete-count">{c.count.toLocaleString()}</span>
        </div>
      ))}
    </div>
    <p className="nfl__chart-note">Zero missing values across all columns. Every join on nflId is safe before a single query runs.</p>
  </div>
);

// ── Component ────────────────────────────────────────────────────────

interface NFLProjectProps {
  theme?: string;
}

const NFLProject: React.FC<NFLProjectProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      gsap.from('.nfl__hero-video', {
        opacity: 0, duration: 1.8, ease: 'power3.out', delay: 0.1
      });
      gsap.from('.nfl__title', {
        y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.25
      });
      gsap.from('.nfl__descriptor', {
        y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.4
      });
      gsap.from('.nfl__meta-item', {
        y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.55
      });

      gsap.set('.nfl-fade-up', { y: 30, opacity: 0 });
      gsap.set('.nfl-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.nfl-fade-up', {
        onEnter: (els) => gsap.to(els, { y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out' }),
        start: 'top 90%', once: true,
      });
      ScrollTrigger.batch('.nfl-fade-scale', {
        onEnter: (els) => gsap.to(els, { scale: 1, opacity: 1, stagger: 0.07, duration: 1, ease: 'power3.out' }),
        start: 'top 88%', once: true,
      });

    }, rootRef);
    return () => ctx.revert();
  }, []);

  return (
    <div className="nfl" ref={rootRef}>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="nfl__hero">
        <video
          className="nfl__hero-video"
          src="/content/nfl/rugby.mp4"
          autoPlay muted loop playsInline
        />
        <div className="nfl__hero-overlay" />
      </section>

      <div className="nfl__container">

        {/* ── Header ───────────────────────────────────────────────── */}
        <header className="nfl__header">
          <h1 className="nfl__title">Decoding Player Behavior</h1>
          <p className="nfl__descriptor">
            A data analysis and visualization project for the NFL Big Data Bowl 2025. Translating 70M+ RFID tracking coordinates into behavioral patterns, then documenting the entire process in a 40-page guidebook adopted by Indiana University's Sports Data Analytics course.
          </p>
          <div className="nfl__meta">
            {[
              { label: 'Type',  value: 'Data Analytics + Info Design' },
              { label: 'Year',  value: '2025' },
              { label: 'Role',  value: 'HCI Research · Team of 3' },
              { label: 'Tools', value: 'Python · R · Pandas · ggplot2' },
            ].map(({ label, value }) => (
              <div className="nfl__meta-item" key={label}>
                <div className="nfl__meta-label">{label}</div>
                <div className="nfl__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* ── TL;DR + Outcomes ─────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">overview</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              Seventy million data points. The hard part was deciding which ones mattered.
            </p>
            <ul className="nfl__outcomes">
              {OUTCOMES.map(o => (
                <li className="nfl__outcome nfl-fade-up" key={o.tag}>
                  <div className="nfl__outcome-dot" />
                  <div>
                    <span className="nfl__outcome-tag">{o.tag}</span>
                    <span className="nfl__outcome-strong"> {o.title}</span>
                    <span className="nfl__outcome-desc"> {o.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="nfl__stat-strip">
            {[
              { num: '70M+',   label: 'tracking data points' },
              { num: '10 Hz',  label: 'capture rate' },
              { num: '9',      label: 'weeks of game data' },
              { num: '1,697',  label: 'players tracked' },
            ].map(({ num, label }) => (
              <div className="nfl__stat-item nfl-fade-up" key={label}>
                <span className="nfl__stat-num">{num}</span>
                <span className="nfl__stat-label">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── The Dataset ──────────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">data sources</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              Four files. One field. Every player, every 0.1 seconds.
            </p>
            <p className="nfl__body nfl-fade-up">
              The Next Gen Stats system captures RFID data from chips embedded in player shoulder pads and the football itself. The data is relational: games contain plays, plays contain tracking events, tracking events reference players. At 10 frames per second, a single week of games generates roughly 8 million rows.
            </p>
          </div>
          <div className="nfl__dataset-strip">
            {DATASETS.map(d => (
              <div className="nfl__dataset-card nfl-fade-up" key={d.file}>
                <span className="nfl__dataset-file">{d.file}</span>
                <span className="nfl__dataset-dims">{d.dims}</span>
                <p className="nfl__dataset-desc">{d.desc}</p>
              </div>
            ))}
          </div>

          {/* How it's captured */}
          <div className="nfl__analysis-label nfl-fade-up" style={{ marginTop: '3rem' }}>how it's captured</div>
          <div className="nfl__equipment-split nfl-fade-up">
            <div className="nfl__equipment-img">
              <img src={`${I}image24.png`} alt="RFID chips embedded in NFL shoulder pads" />
            </div>
            <div>
              <p className="nfl__body" style={{ marginBottom: '1.25rem' }}>
                Each player wears an RFID chip in both shoulder pads. A third chip is embedded in the football itself.
                Antenna arrays installed at multiple points around every NFL stadium triangulate all chips simultaneously,
                10 times per second — producing a precise (x, y) coordinate for every player and the ball on every tick.
              </p>
              <div className="nfl__data-points-grid">
                {[
                  { label: 'X / Y Position', desc: 'Field coordinates: x = 0 at left end zone, 120 at right. y = 0 at one sideline, 53.3 at the other.' },
                  { label: 'Speed & Accel', desc: 'Derived from position deltas between frames. Units: yards/sec and yards/sec². Acceleration is not the raw chip data.' },
                  { label: 'Orientation vs Direction', desc: 'Orientation = where the body faces. Direction = where the player is moving. They can point in completely different directions.' },
                  { label: '10 Hz Sampling', desc: 'One frame every 0.1 seconds. A 5-second play produces 50 frames per player — 1,100 coordinate readings for the full 22-player field.' },
                ].map(pt => (
                  <div key={pt.label} className="nfl__data-point-card">
                    <div className="nfl__data-point-label">{pt.label}</div>
                    <p className="nfl__data-point-desc">{pt.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="nfl__field-img-wrap nfl-fade-scale">
            <img src={`${I}image23.png`} alt="Tracking data at sparse, moderate, and crowded formation densities" />
            <span className="nfl__img-caption">Field coverage at different formation densities — sparse, moderate, and crowded pre-snap alignments</span>
          </div>
        </section>

        {/* ── The Pipeline ─────────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">loading & cleaning</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              Before you can see anything, you have to trust the data.
            </p>
            <p className="nfl__body nfl-fade-up">
              Four CSV files loaded into pandas dataframes. Missing values identified across all columns. Critical identifier fields are dropped if absent since they cannot be imputed. Numeric columns use median imputation, which is more robust to outliers than mean. The tracking data alone is 8M rows per week, so the pipeline had to be efficient before analysis could begin.
            </p>
          </div>

          <div className="nfl__code-pair nfl-fade-up">
            <CodeBlock
              lang="Python"
              filename="data_loading.py"
              code={`# Import required libraries
import pandas as pd
import matplotlib.pyplot as plt
import seaborn as sns
import numpy as np

# Load datasets
df_games = pd.read_csv("games.csv")
df_players = pd.read_csv("players.csv")
df_plays = pd.read_csv("plays.csv")
df_tracking_week_1 = pd.read_csv("tracking_week_1.csv")

# Display basic info
print(f"Games: {df_games.shape}")
print(f"Players: {df_players.shape}")
print(f"Tracking Data: {df_tracking_week_1.shape}")`}
            />
            <div className="nfl__code-insight nfl-fade-up">
              <span className="nfl__insight-label">what this reveals</span>
              <p className="nfl__body">The shape output immediately signals the scale problem: tracking data for a single week is 50 times larger than the plays file. Any join across these files has to be designed around that asymmetry before any analysis starts.</p>
            </div>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2rem' }}>
            <CodeBlock
              lang="Python"
              filename="data_cleaning.py"
              code={`# Check for missing values
for name, df in dfs.items():
    print(f"{name} Missing Values:\\n{df.isnull().sum()}\\n")

# Drop rows with critical missing values
critical_columns = ['gameId', 'playId', 'nflId']
for name, df in dfs.items():
    df.dropna(subset=[col for col in critical_columns
                      if col in df.columns], inplace=True)

# Fill numeric columns with median
for name, df in dfs.items():
    numeric_cols = df.select_dtypes(include=['number']).columns
    df[numeric_cols] = df[numeric_cols].fillna(
        df[numeric_cols].median()
    )`}
            />
            <DataCompletenessChart />
          </div>
        </section>

      </div>

      {/* ── EDA (alternate background) ───────────────────────────── */}
      <section className="nfl__eda-section">
        <div className="nfl__container">

          <div className="nfl__section-label nfl-fade-up">data exploration</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              The first charts you make are never for the audience. They are for you.
            </p>
            <p className="nfl__body nfl-fade-up">
              EDA is the investigative pass before committing to a method. Summary statistics, distribution plots, correlation matrices. Each answers a different question: is the data reliable? Are these variables related? Where are the anomalies? The visualization choices here are design decisions as much as analytical ones.
            </p>
          </div>

          <div className="nfl__feature-img-wrap nfl-fade-scale">
            <img src={`${I}image12.png`} alt="Summary statistics with gradient encoding" />
            <span className="nfl__img-caption">Summary statistics table with gradient encoding by column range</span>
          </div>

          {/* Speed distribution */}
          <div className="nfl__analysis-subsection">
            <div className="nfl__analysis-label nfl-fade-up">Speed distribution</div>
            <p className="nfl__body nfl-fade-up">Speed values are right-skewed across all 9 weeks: most tracking frames capture players at low speed (lining up, reacting to snap) with a long tail of sprint events. Consistent distributions across weeks validated data quality before deeper analysis. A flat or shifted week would signal RFID malfunction or data corruption.</p>
            <div className="nfl__code-pair" style={{ marginTop: '1.5rem' }}>
              <CodeBlock
                lang="Python"
                filename="speed_distribution.py"
                code={`# Speed distribution across all 9 weeks
fig, axes = plt.subplots(3, 3, figsize=(15, 12))
axes = axes.flatten()

for week in range(1, 10):
    df = pd.read_csv(f"tracking_week_{week}.csv")
    axes[week-1].hist(df['s'], bins=30,
                      color='skyblue', edgecolor='black')
    axes[week-1].set_title(f'Week {week}')
    axes[week-1].set_xlabel('Speed (yards/sec)')
    axes[week-1].set_ylabel('Frequency')

plt.tight_layout()
plt.show()`}
              />
              <SpeedDistributionChart />
            </div>
          </div>

          {/* Correlation */}
          <div className="nfl__analysis-subsection">
            <div className="nfl__analysis-label nfl-fade-up">Correlation</div>
            <p className="nfl__body nfl-fade-up">Speed and distance correlate at 0.98, nearly perfect and expected. The more useful number: acceleration correlates at only 0.58. That gap is where position roles live. An offensive lineman and a wide receiver can run at similar speeds with completely different acceleration profiles. The 0.42 gap is the signal.</p>
            <div className="nfl__code-pair" style={{ marginTop: '1.5rem' }}>
              <CodeBlock
                lang="Python"
                filename="correlation_analysis.py"
                code={`# Correlation: speed, acceleration, distance
corr = df_tracking_week_1[['s', 'a', 'dis']].corr()

sns.heatmap(corr, annot=True, cmap='coolwarm')
plt.title("Correlation Matrix")
plt.show()`}
              />
              <CorrelationMatrixChart />
            </div>
          </div>

        </div>
      </section>

      <div className="nfl__container">

        {/* ── Player Positioning ───────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">spatial heatmaps</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              Where a player stands at the snap is more tactical than physical.
            </p>
            <p className="nfl__body nfl-fade-up">
              KDE heatmaps aggregate millions of position coordinates into density contours, turning a scatter of 8 million unreadable points into a spatial fingerprint. Offense clusters at the line of scrimmage, receivers spread wide, defensive backs sit 7 to 12 yards off in coverage. The heatmap does not tell you what happened. It tells you what teams plan for.
            </p>
          </div>

          <div className="nfl__img-pair nfl-fade-scale">
            <div className="nfl__img-wrap">
              <img src={`${I}image18.png`} alt="All-position density map" />
              <span className="nfl__img-caption">All-position density map</span>
            </div>
            <div className="nfl__img-wrap">
              <img src={`${I}image31.png`} alt="Defensive concentration heatmap" />
              <span className="nfl__img-caption">Defensive concentration zones</span>
            </div>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2.5rem' }}>
            <CodeBlock
              lang="Python"
              filename="heatmap_analysis.py"
              code={`# KDE heatmap of player positions
heatmap_data = df_tracking_week_1[['x', 'y']].dropna().sample(
    n=500, random_state=42
)

plt.figure(figsize=(12, 6))
sns.kdeplot(x=heatmap_data['x'], y=heatmap_data['y'],
            cmap='Blues', fill=True)
plt.xlim(0, 120)
plt.ylim(0, 53.3)
plt.xlabel("Field Length (yards)")
plt.ylabel("Field Width (yards)")
plt.title("Player Position Heatmap")

# Field yard markers
for x in range(0, 130, 10):
    plt.axvline(x, color='gray', linestyle='--', alpha=0.6)
plt.show()`}
            />
            <div className="nfl__code-insight nfl-fade-up">
              <span className="nfl__insight-label">why KDE over scatter</span>
              <p className="nfl__body">A raw scatter of 8M points is visually meaningless. KDE smooths the density and reveals spatial structure. Blues encodes concentration without implying categorical difference, which a categorical colormap would suggest incorrectly. The visualization choice is an argument.</p>
            </div>
          </div>
        </section>

        {/* ── Formation Recognition ────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">formation classifier</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              87% accuracy. 22 players. Raw coordinates. No labels given.
            </p>
            <div className="nfl-fade-up">
              <p className="nfl__body">
                K-means clustering on pre-snap spatial features: QB depth, receiver spread, defenders in the box. The model learns that a shotgun formation is not just a position value, it is a cluster of spatial relationships. Labels from the plays dataset supervised the final classifier.
              </p>
              <div className="nfl__formation-cols">
                <div>
                  <div className="nfl__formation-label">Offensive</div>
                  <ul className="nfl__formation-list">
                    <li>Shotgun: QB stands several yards behind center</li>
                    <li>Singleback: one RB in backfield, play-action base</li>
                    <li>I-Formation: FB plus RB for power runs</li>
                    <li>Trips: three WRs on one side of the formation</li>
                  </ul>
                </div>
                <div>
                  <div className="nfl__formation-label">Defensive</div>
                  <ul className="nfl__formation-list">
                    <li>4-3: four linemen, three linebackers, balanced</li>
                    <li>3-4: three linemen, four LBs for versatile blitzing</li>
                    <li>Nickel: fifth DB replaces a linebacker</li>
                    <li>Dime: sixth DB, obvious passing situations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="nfl__img-pair nfl-fade-scale" style={{ marginTop: '2.5rem' }}>
            <div className="nfl__img-wrap">
              <img src={`${I}image1.png`} alt="Offensive formation breakdown" />
              <span className="nfl__img-caption">Offensive formation breakdown</span>
            </div>
            <div className="nfl__img-wrap">
              <img src={`${I}image6.png`} alt="Defensive formation analysis" />
              <span className="nfl__img-caption">Defensive formation analysis</span>
            </div>
          </div>
        </section>

        {/* ── Movement Efficiency ──────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">movement patterns</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              How straight did you go? A ratio that separates routes from blocks.
            </p>
            <p className="nfl__body nfl-fade-up">
              Euclidean distance divided by total distance traveled. A receiver on a slant scores 0.3. An offensive lineman engaging directly scores 0.8. The number makes position-specific movement visible in data that was just x and y coordinates. It is a derived feature, and constructing it required knowing enough football to know the question was worth asking.
            </p>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2.5rem' }}>
            <CodeBlock
              lang="Python"
              filename="efficiency_analysis.py"
              code={`# Compute total vs. straight-line distance
total_dist = df_tracking_week_1.groupby(
    ['gameId', 'playId', 'nflId']
)['dis'].sum().reset_index()

start_pos = df_tracking_week_1.groupby(
    ['gameId', 'playId', 'nflId']
).first().reset_index()
end_pos = df_tracking_week_1.groupby(
    ['gameId', 'playId', 'nflId']
).last().reset_index()

start_pos['euclidean_dist'] = np.sqrt(
    (end_pos['x'] - start_pos['x'])**2 +
    (end_pos['y'] - start_pos['y'])**2
)

features = total_dist.merge(
    start_pos[['gameId','playId','nflId','euclidean_dist']],
    on=['gameId', 'playId', 'nflId']
)
features['movement_efficiency'] = (
    features['euclidean_dist'] / features['dis']
)`}
            />
            <div className="nfl__code-insight nfl-fade-up">
              <span className="nfl__insight-label">the design decision</span>
              <p className="nfl__body">Movement efficiency is not a column in the dataset. It had to be constructed by hypothesizing that receivers and linemen have structurally different path shapes. The question preceded the code. That is what domain knowledge looks like in practice.</p>
            </div>
          </div>
        </section>

        {/* ── QB Decision Zones ────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">quarterback analysis</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              QBs who hold past 3 seconds are 23% less accurate.
            </p>
            <p className="nfl__body nfl-fade-up">
              Time-to-throw is computed by counting frames between the snap event and the pass release event. Release zone density maps show where quarterbacks felt comfortable. Lateral drift is visible in the scatter as pressure disrupts pocket position.
            </p>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2.5rem' }}>
            <CodeBlock
              lang="Python"
              filename="qb_analysis.py"
              code={`# QB decision zones on pass plays
GAME_ID = 2022090800
df_game = df_tracking_week_1[
    df_tracking_week_1['gameId'] == GAME_ID
]

df_merged = df_game.merge(
    df_players[['nflId', 'position']], on='nflId', how='left'
)
df_qb = df_merged[df_merged['position'] == 'QB']

plt.figure(figsize=(12, 6))
for qb in df_qb['nflId'].unique()[:3]:
    qb_data = df_qb[df_qb['nflId'] == qb]
    plt.scatter(qb_data['x'], qb_data['y'],
                label=f'QB {qb}', alpha=0.6)

plt.xlim(0, 120)
plt.ylim(0, 53.3)
plt.title("QB Decision Zones")
plt.legend()
plt.show()`}
            />
            <div className="nfl__code-img-wrap nfl-fade-scale">
              <img src={`${I}image21.png`} alt="QB decision zones scatter plot" />
              <span className="nfl__img-caption">Release zones across a full game: pocket depth and lateral drift under pressure</span>
            </div>
          </div>
        </section>

      </div>

      {/* ── Core finding callout ─────────────────────────────────── */}
      <section className="nfl__finding-callout">
        <div className="nfl__container">
          <div className="nfl__split">
            <p className="nfl__section-label nfl-fade-up" style={{ color: 'rgba(255,255,255,0.4)' }}>key finding</p>
            <div>
              <p className="nfl__finding-quote nfl-fade-up">
                Speed and distance correlate at 0.98.<br />
                The signal is in the 0.02 that doesn't.
              </p>
              <p className="nfl__body nfl-fade-up" style={{ color: 'rgba(255,255,255,0.6)', marginTop: '1.25rem' }}>
                Perfect correlation means two variables carry the same information. The gap, where acceleration only reaches 0.58, is where position-specific behavior lives. A tight end and a cornerback can run at the same speed with completely different acceleration profiles. That difference is what the analysis was trying to surface.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="nfl__container">

        {/* ── Rushing Analysis in R ────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">rushing yards · R</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              Not every answer comes from Python.
            </p>
            <p className="nfl__body nfl-fade-up">
              The rushing yards analysis runs in R with ggplot2, which handles statistical distribution visualization differently than matplotlib. The median is zero across all plays because most records in the dataset are not rushing attempts. That zero is not a data gap. It is a correct answer, and knowing the difference is the skill.
            </p>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2.5rem' }}>
            <CodeBlock
              lang="R"
              filename="rushing_stats.R"
              code={`# Rushing yards summary statistics
player_stats <- player_plays %>%
  summarise(
    Mean_Rushing_Yards = mean(rushingYards, na.rm = TRUE),
    Median_Rushing_Yards = median(rushingYards, na.rm = TRUE),
    SD_Rushing_Yards = sd(rushingYards, na.rm = TRUE),
    Mode_Rushing_Yards = as.numeric(names(
      sort(table(rushingYards), decreasing = TRUE)[1]
    ))
  )

print(player_stats)`}
            />
            <div className="nfl__code-img-wrap nfl-fade-scale">
              <img src={`${I}image2.png`} alt="Rushing yards statistics output" />
              <span className="nfl__img-caption">Output: Mean = 0.087, Median = 0, SD = 1.098</span>
            </div>
          </div>

          <div className="nfl__code-pair" style={{ marginTop: '2rem' }}>
            <div className="nfl__code-img-wrap nfl-fade-scale">
              <img src={`${I}image4.png`} alt="Rushing yards distribution histogram" />
              <span className="nfl__img-caption">Distribution: spike at zero, right tail of explosive runs beyond 10 yards</span>
            </div>
            <CodeBlock
              lang="R"
              filename="rushing_distribution.R"
              code={`# Distribution with mean and median reference lines
ggplot(player_plays, aes(x = rushingYards)) +
  geom_histogram(binwidth = 5, fill = "purple",
                 color = "black", alpha = 0.7) +
  theme_minimal() +
  labs(title = "Distribution of Rushing Yards",
       x = "Rushing Yards", y = "Frequency") +
  geom_vline(aes(xintercept = mean(rushingYards,
                 na.rm = TRUE)),
             color = "red", linetype = "dashed",
             linewidth = 1) +
  geom_vline(aes(xintercept = median(rushingYards,
                 na.rm = TRUE)),
             color = "blue", linetype = "dashed",
             linewidth = 1)`}
            />
          </div>
        </section>

        {/* ── The Deliverable ──────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">the guidebook</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              The analysis was the work. The guidebook was the point.
            </p>
            <p className="nfl__body nfl-fade-up">
              A 40-page document that teaches the complete pipeline: data loading, cleaning, exploratory analysis, spatial heatmaps, formation recognition, and predictive modeling. Adopted by Indiana University's Sports Data Analytics course, used by 34 students. Reduced competition onboarding by 2 weeks by consolidating every visualization example into a single resource. Every code block is annotated. Every chart type is justified.
            </p>
          </div>
          <div className="nfl__artifact-links nfl-fade-up">
            <a
              href="https://sii-sports-data-solutions-lab.github.io/home/#/projects/nfl-big-data-bowl-guidebook"
              target="_blank"
              rel="noopener noreferrer"
              className="nfl__artifact-link nfl__artifact-link--primary"
            >
              View the guidebook
            </a>
            <a
              href="https://www.kaggle.com/competitions/nfl-big-data-bowl-2025/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="nfl__artifact-link"
            >
              View the competition
            </a>
          </div>
        </section>

        {/* ── Reflections ──────────────────────────────────────────── */}
        <section className="nfl__section">
          <div className="nfl__section-label nfl-fade-up">what I learned</div>
          <div className="nfl__split">
            <p className="nfl__statement nfl-fade-up">
              What data analysis taught a UX designer about information hierarchy.
            </p>
            <div>
              {REFLECTIONS.map(({ num, text }) => (
                <div className="nfl__reflect-item nfl-fade-up" key={num}>
                  <span className="nfl__reflect-num">{num}</span>
                  <p className="nfl__body">{text}</p>
                </div>
              ))}
              <div className="nfl__status nfl-fade-up">
                <div className="nfl__status-row">
                  <span className="nfl__status-label">Competition</span>
                  <span className="nfl__status-value">NFL Big Data Bowl 2025 · Kaggle</span>
                </div>
                <div className="nfl__status-row">
                  <span className="nfl__status-label">Guidebook</span>
                  <span className="nfl__status-value">Published via SII Sports Data Solutions Lab</span>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default NFLProject;
